import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'motion/react';
import { 
  Network, 
  CreditCard, 
  Rss, 
  Users, 
  Shield, 
  ChevronRight,
  BarChart3,
  MessageSquare,
  Activity,
  ArrowRight,
  CheckCircle2,
  Wallet,
  Building2,
  Globe,
  Lock,
  FileText,
  BadgeCheck,
  Fingerprint,
  Download,
  Mail,
  MessageCircle,
  MessagesSquare,
  LayoutTemplate,
  Send,
  ThumbsUp,
  MessageSquareReply,
  CheckSquare,
  Calendar,
  Target,
  LifeBuoy,
  HeartHandshake,
  TrendingUp,
  Landmark,
  Vote,
  Blocks,
  FolderLock,
  LayoutList
} from 'lucide-react';
import { Badge, Button, Card } from '../components/ui';
import { PublicHeader } from '../components/shell/PublicHeader';
import { PublicFooter } from '../components/shell/PublicFooter';
import { cn } from '../lib/utils';
import { SEO } from '../components/SEO';

// --- Mock UI Components for "Pictures" ---



const MockWindow = ({ children, className }: { children: React.ReactNode, className?: string }) => (
  <motion.div 
    whileHover={{
      scale: 1.03,
      rotateY: 2,
      rotateX: 2,
      boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 20px rgba(99, 102, 241, 0.2)"
    }}
    transition={{ type: "spring", stiffness: 300, damping: 20 }}
    className={cn("bg-[#0f172a] rounded-2xl overflow-hidden shadow-[0_20px_50px_-12px_rgba(0,0,0,0.5)] border border-slate-800/60 ring-1 ring-white/10", className)}
  >
    <div className="h-10 bg-slate-900/50 border-b border-white/5 flex items-center px-4 gap-2 backdrop-blur-sm">
      <div className="w-2.5 h-2.5 rounded-full bg-rose-500/80" />
      <div className="w-2.5 h-2.5 rounded-full bg-amber-500/80" />
      <div className="w-2.5 h-2.5 rounded-full bg-emerald-500/80" />
    </div>
    <div className="p-6 bg-gradient-to-b from-slate-900 to-[#0f172a]">
      {children}
    </div>
  </motion.div>
);

const HierarchyMock = () => (
  <MockWindow className="w-full">
    <div className="flex items-center justify-between mb-6">
      <div>
        <h3 className="text-white font-bold text-lg tracking-tight">Global Network</h3>
        <p className="text-slate-400 text-xs">Managing 48 regional chapters</p>
      </div>
      <Badge className="bg-brand-primary/20 text-brand-primary border-brand-primary/30">Tier: Enterprise</Badge>
    </div>
    <div className="space-y-3">
      {/* Root Node */}
      <div className="p-3 bg-white/5 rounded-xl border border-white/10 flex items-center gap-4 dark:bg-slate-900">
        <div className="bg-brand-primary/20 p-2 rounded-lg text-brand-primary"><Globe size={18} /></div>
        <div className="flex-1">
          <div className="font-semibold text-white text-sm">International Headquarters</div>
          <div className="text-slate-500 dark:text-slate-400 text-xs">Parent Org</div>
        </div>
        <div className="text-right">
          <div className="text-white text-sm font-bold">12,450</div>
          <div className="text-slate-500 dark:text-slate-400 text-[10px] uppercase tracking-wider">Members</div>
        </div>
      </div>
      {/* Child Nodes */}
      <div className="pl-6 border-l-2 border-white/10 space-y-3 ml-4 relative">
        <div className="absolute top-0 -left-[2px] w-[2px] h-1/2 bg-brand-primary" />
        <div className="p-3 bg-white/5 rounded-xl border border-white/5 flex items-center gap-4 relative dark:bg-slate-900">
          <div className="absolute -left-[25px] w-4 h-[2px] bg-white/10 dark:bg-slate-900" />
          <div className="bg-blue-500/20 p-2 rounded-lg text-blue-400"><Building2 size={16} /></div>
          <div className="flex-1">
             <div className="font-semibold text-white text-sm">North America Region</div>
             <div className="text-slate-500 dark:text-slate-400 text-xs text-green-400 flex items-center gap-1"><CheckCircle2 size={12} /> Active</div>
          </div>
          <div className="text-white text-sm font-bold">4,120</div>
        </div>
        <div className="p-3 bg-white/5 rounded-xl border border-white/5 flex items-center gap-4 relative opacity-80 dark:bg-slate-900">
          <div className="absolute -left-[25px] w-4 h-[2px] bg-white/10 dark:bg-slate-900" />
          <div className="bg-blue-500/20 p-2 rounded-lg text-blue-400"><Building2 size={16} /></div>
          <div className="flex-1">
             <div className="font-semibold text-white text-sm">European Union</div>
             <div className="text-slate-500 dark:text-slate-400 text-xs text-green-400 flex items-center gap-1"><CheckCircle2 size={12} /> Active</div>
          </div>
          <div className="text-white text-sm font-bold">3,890</div>
        </div>
      </div>
    </div>
  </MockWindow>
);

const LedgerMock = () => (
  <MockWindow className="w-full overflow-hidden relative">
    <div className="absolute -right-20 -top-20 w-64 h-64 bg-emerald-500/10 blur-3xl rounded-full" />
    <div className="flex items-center justify-between mb-8 relative z-10">
      <div>
        <h3 className="text-white font-bold text-lg tracking-tight">Automated Splits</h3>
        <p className="text-slate-400 text-xs">Stripe Connect Integration</p>
      </div>
      <div className="p-2 bg-emerald-500/20 rounded-lg text-emerald-400"><Wallet size={20} /></div>
    </div>
    
    <div className="space-y-6 relative z-10">
      <div className="p-4 bg-emerald-500/10 rounded-xl border border-emerald-500/20">
        <div className="text-slate-400 text-xs uppercase tracking-wider font-mono mb-1">Incoming Payment</div>
        <div className="text-3xl font-black text-white">$1,000.00</div>
        <div className="text-emerald-400 text-xs mt-1">From: Member Annual Dues</div>
      </div>

      <div className="space-y-2">
        <div className="text-xs font-mono text-slate-500 dark:text-slate-400 uppercase tracking-widest pl-2">Contract Split Execution</div>
        
        {/* Split items */}
        <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg border border-white/5 dark:bg-slate-900">
          <div className="flex items-center gap-3">
             <div className="w-6 h-6 rounded bg-brand-primary/20 flex items-center justify-center text-brand-primary text-xs font-bold">HQ</div>
             <span className="text-sm font-medium text-white">Global Headquarters</span>
          </div>
          <div className="text-right">
            <div className="text-white font-mono text-sm">$300.00</div>
            <div className="text-slate-500 dark:text-slate-400 text-[10px]">30% Allocation</div>
          </div>
        </div>

        <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg border border-white/5 dark:bg-slate-900">
          <div className="flex items-center gap-3">
             <div className="w-6 h-6 rounded bg-blue-500/20 flex items-center justify-center text-blue-400 text-xs font-bold">CH</div>
             <span className="text-sm font-medium text-white">Local Chapter</span>
          </div>
          <div className="text-right">
            <div className="text-emerald-400 font-mono text-sm font-bold">$700.00</div>
            <div className="text-slate-500 dark:text-slate-400 text-[10px]">70% Allocation</div>
          </div>
        </div>
      </div>
    </div>
  </MockWindow>
);

const PulseMock = () => (
  <MockWindow className="w-full">
    <div className="flex items-center justify-between mb-6">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-rose-500/20 rounded-lg text-rose-400"><Activity size={20} /></div>
        <div>
          <h3 className="text-white font-bold text-lg tracking-tight">Pulse Feed</h3>
          <p className="text-slate-400 text-xs">Real-time engagement</p>
        </div>
      </div>
    </div>

    <div className="space-y-4">
      {/* Alert Card */}
      <div className="p-4 bg-gradient-to-r from-rose-500/10 to-transparent border-l-4 border-rose-500 rounded-r-xl border-y border-r border-white/5">
        <div className="flex justify-between items-start mb-2">
          <Badge className="bg-rose-500/20 text-rose-400 border-none px-2 py-0 h-5 text-[10px]">URGENT</Badge>
          <span className="text-slate-500 dark:text-slate-400 text-xs">Just now</span>
        </div>
        <h4 className="text-white font-bold text-sm mb-1">Emergency Protocol Update</h4>
        <p className="text-slate-400 text-xs line-clamp-2">Please review the updated governance documents uploaded to the vault. Acknowledgment required.</p>
        <div className="mt-3 flex items-center gap-4">
          <div className="flex -space-x-2">
            {[1,2,3].map(i => <div key={i} className="w-6 h-6 rounded-full border border-slate-900 bg-slate-700" />)}
          </div>
          <span className="text-xs text-rose-400 font-medium">42 acknowledged</span>
        </div>
      </div>

      {/* Standard Post */}
      <div className="p-4 bg-white/5 rounded-xl border border-white/5 dark:bg-slate-900">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-8 h-8 rounded-full bg-brand-primary/20 flex items-center justify-center text-brand-primary font-bold text-xs">TC</div>
          <div>
            <div className="text-white text-sm font-medium">Top Council</div>
            <div className="text-slate-500 dark:text-slate-400 text-[10px]">Published a Newsletter • 2h ago</div>
          </div>
        </div>
        <p className="text-slate-300 text-sm mb-3">Q3 Review highlights are out! Excellent growth across all European orgs.</p>
        <div className="h-24 bg-white/5 rounded-lg border border-white/5 flex items-center justify-center text-slate-500 dark:text-slate-400 dark:bg-slate-900">
           <BarChart3 size={24} />
        </div>
      </div>
    </div>
  </MockWindow>
);

const VaultMock = () => (
  <MockWindow className="w-full overflow-hidden relative">
    <div className="absolute -left-20 -top-20 w-64 h-64 bg-violet-500/10 blur-3xl rounded-full" />
    <div className="flex items-center justify-between mb-8 relative z-10">
      <div>
        <h3 className="text-white font-bold text-lg tracking-tight">Secure Vault</h3>
        <p className="text-slate-400 text-xs">Policy & Document Control</p>
      </div>
      <div className="p-2 bg-violet-500/20 rounded-lg text-violet-400"><Lock size={20} /></div>
    </div>
    
    <div className="space-y-4 relative z-10">
      {[
        { name: 'Global_Bylaws_2026.pdf', size: '2.4 MB', type: 'GLOBAL', date: 'Just now' },
        { name: 'Regional_Ops_Manual.docx', size: '1.1 MB', type: 'SHARED_DOWN', date: '2 days ago' },
        { name: 'Node_Financials_Q1.xlsx', size: '4.8 MB', type: 'PRIVATE', date: '1 week ago' },
      ].map((file, i) => (
        <div key={i} className="flex items-center justify-between p-4 bg-white/5 rounded-xl border border-white/5 hover:bg-white/10 transition-colors dark:bg-slate-900">
          <div className="flex items-center gap-3">
             <div className="w-10 h-10 rounded-lg bg-slate-800 border border-slate-700 flex items-center justify-center text-slate-400">
               <FileText size={20} />
             </div>
             <div>
               <div className="text-sm font-medium text-white">{file.name}</div>
               <div className="text-[10px] text-slate-500 dark:text-slate-400 font-mono mt-0.5">{file.size} • {file.date}</div>
             </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <Badge className={cn(
              "text-[9px] px-2 py-0 h-4 border-none uppercase",
              file.type === 'GLOBAL' ? "bg-amber-500/20 text-amber-400" : 
              file.type === 'PRIVATE' ? "bg-rose-500/20 text-rose-400" : "bg-blue-500/20 text-blue-400"
            )}>{file.type}</Badge>
            <Download size={14} className="text-slate-500 dark:text-slate-400 hover:text-white cursor-pointer transition-colors" />
          </div>
        </div>
      ))}
    </div>
  </MockWindow>
);

const DirectoryMock = () => (
  <MockWindow className="w-full overflow-hidden relative">
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-cyan-500/5 blur-[100px] rounded-full" />
    <div className="flex items-center justify-between mb-8 relative z-10">
      <div>
        <h3 className="text-white font-bold text-lg tracking-tight">Identity & Directory</h3>
        <p className="text-slate-400 text-xs">Vetted Member Passport</p>
      </div>
      <div className="p-2 bg-cyan-500/20 rounded-lg text-cyan-400"><Fingerprint size={20} /></div>
    </div>
    
    <div className="relative z-10 bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl border border-white/10 p-1 mb-4">
      <div className="bg-slate-900/50 rounded-xl p-5 backdrop-blur-md border border-white/5">
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-4">
             <div className="w-14 h-14 rounded-full bg-gradient-to-tr from-cyan-500 to-blue-500 p-[2px]">
               <div className="w-full h-full rounded-full bg-slate-900 flex items-center justify-center font-bold text-white text-xl border-2 border-slate-900">
                 RT
               </div>
             </div>
             <div>
               <div className="flex items-center gap-2">
                 <h4 className="text-lg font-bold text-white tracking-tight">Richard Thorne</h4>
                 <BadgeCheck size={16} className="text-cyan-400" />
               </div>
               <div className="text-xs text-cyan-400 font-mono mt-1">Level 3 Vetted</div>
             </div>
          </div>
          <Badge variant="gray" className="border-white/10 text-slate-400 text-[10px]">ID: 8092-AX</Badge>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="p-3 bg-white/5 rounded-lg border border-white/5 dark:bg-slate-900">
            <div className="text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-1 font-mono">Current Org</div>
            <div className="text-sm text-white font-medium">Western Region HQ</div>
          </div>
          <div className="p-3 bg-white/5 rounded-lg border border-white/5 dark:bg-slate-900">
            <div className="text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-1 font-mono">Role</div>
            <div className="text-sm text-white font-medium">Org Administrator</div>
          </div>
        </div>
      </div>
    </div>
    
    <div className="space-y-2 relative z-10">
      <div className="flex justify-between items-center text-[10px] text-slate-500 dark:text-slate-400 font-mono uppercase tracking-widest px-2 mb-2">
        <span>Recent Network Activity</span>
      </div>
      {[
        { event: 'Authorized access to Shared Vault', time: '2h ago' },
        { event: 'Acknowledged Pulse #1142', time: '1d ago' },
      ].map((log, i) => (
        <div key={i} className="flex items-center gap-3 px-3 py-2 text-xs">
          <div className="w-1.5 h-1.5 rounded-full bg-cyan-500/50" />
          <span className="text-slate-300 flex-1">{log.event}</span>
          <span className="text-slate-600 dark:text-slate-400 font-mono text-[10px]">{log.time}</span>
        </div>
      ))}
    </div>
  </MockWindow>
);

const NewsletterMock = () => (
  <MockWindow className="w-full overflow-hidden relative">
    <div className="absolute -right-20 -top-20 w-64 h-64 bg-fuchsia-500/10 blur-3xl rounded-full" />
    <div className="flex items-center justify-between mb-8 relative z-10">
      <div>
        <h3 className="text-white font-bold text-lg tracking-tight">Newsletter Studio</h3>
        <p className="text-slate-400 text-xs">Drag & Drop Editor</p>
      </div>
      <div className="p-2 bg-fuchsia-500/20 rounded-lg text-fuchsia-400"><LayoutTemplate size={20} /></div>
    </div>
    
    <div className="relative z-10 flex gap-4">
      <div className="w-16 space-y-2 border-r border-white/10 pr-4">
         <div className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-slate-500 dark:text-slate-400 hover:text-white hover:bg-white/10 transition-colors dark:bg-slate-900"><LayoutTemplate size={16} /></div>
         <div className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-slate-500 dark:text-slate-400 hover:text-white hover:bg-white/10 transition-colors dark:bg-slate-900"><FileText size={16} /></div>
         <div className="w-10 h-10 rounded-lg bg-fuchsia-500/20 border border-fuchsia-500/30 flex items-center justify-center text-fuchsia-400"><Send size={16} /></div>
      </div>
      <div className="flex-1 space-y-3">
         <div className="p-3 border border-dashed border-white/20 rounded-xl flex items-center justify-center text-slate-500 dark:text-slate-400 text-xs h-16 bg-white/[0.02] dark:bg-slate-900">
            Drag Hero Image Here
         </div>
         <div className="p-4 bg-white/5 rounded-xl border border-white/5 dark:bg-slate-900">
            <div className="h-4 w-3/4 bg-white/10 rounded mb-2 dark:bg-slate-900" />
            <div className="h-2 w-full bg-slate-800 rounded mb-1" />
            <div className="h-2 w-5/6 bg-slate-800 rounded" />
         </div>
         <div className="flex items-center justify-between mt-4">
            <span className="text-[10px] text-fuchsia-400 font-mono uppercase tracking-widest flex items-center gap-1"><CheckCircle2 size={10} /> Auto-saved</span>
            <Badge className="bg-fuchsia-500/20 text-fuchsia-400 border-none text-[10px] px-2 py-0.5">Ready to Publish</Badge>
         </div>
      </div>
    </div>
  </MockWindow>
);

const ChatMock = () => (
  <MockWindow className="w-full overflow-hidden relative">
    <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-tr from-sky-500/5 to-transparent blur-3xl pointer-events-none" />
    <div className="flex items-center justify-between mb-6 relative z-10">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-sky-500/20 rounded-lg text-sky-400"><MessageCircle size={20} /></div>
        <div>
          <h3 className="text-white font-bold text-lg tracking-tight">Real-Time Chat</h3>
          <p className="text-slate-400 text-xs">Direct & Group Channels</p>
        </div>
      </div>
    </div>

    <div className="space-y-4 relative z-10 flex flex-col">
      <div className="flex items-end gap-2 text-sm">
         <div className="w-6 h-6 rounded-full bg-slate-800 flex items-center justify-center text-[10px] text-slate-400 font-bold shrink-0">EK</div>
         <div className="p-3 bg-white/5 rounded-2xl rounded-bl-sm border border-white/5 text-slate-300 dark:bg-slate-900">
           Has anyone reviewed the Q3 budget proposals?
         </div>
      </div>
      <div className="flex items-end gap-2 text-sm justify-end">
         <div className="p-3 bg-sky-500/20 rounded-2xl rounded-br-sm border border-sky-500/30 text-sky-100">
           Yes, I added my comments to the shared Vault doc.
         </div>
         <div className="w-6 h-6 rounded-full bg-sky-500 flex items-center justify-center text-[10px] text-white font-bold shrink-0">You</div>
      </div>
      <div className="flex items-end gap-2 text-sm">
         <div className="w-6 h-6 rounded-full bg-brand-primary/20 flex items-center justify-center text-[10px] text-brand-primary font-bold shrink-0">SV</div>
         <div className="p-3 bg-white/5 rounded-2xl rounded-bl-sm border border-white/5 text-slate-300 dark:bg-slate-900">
           Great, I'll sync with HQ on the final numbers.
         </div>
      </div>
      <div className="mt-2 text-[10px] text-slate-500 dark:text-slate-400 font-mono text-center mb-1">General Channel • 3 typing...</div>
      <div className="h-10 border border-white/10 rounded-full bg-slate-900/50 flex items-center px-4 justify-between mt-auto">
        <span className="text-slate-600 dark:text-slate-400 text-xs">Message #general...</span>
        <Send size={14} className="text-slate-500 dark:text-slate-400" />
      </div>
    </div>
  </MockWindow>
);

const DiscussionMock = () => (
  <MockWindow className="w-full overflow-hidden relative">
    <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-orange-500/10 blur-3xl rounded-full" />
    <div className="flex items-center justify-between mb-8 relative z-10">
      <div>
        <h3 className="text-white font-bold text-lg tracking-tight">Message Board</h3>
        <p className="text-slate-400 text-xs">Structured Discussions</p>
      </div>
      <div className="p-2 bg-orange-500/20 rounded-lg text-orange-400"><MessagesSquare size={20} /></div>
    </div>
    
    <div className="space-y-3 relative z-10">
      {[
        { title: 'New Onboarding Process Feedback', author: 'Marcus A.', replies: 12, likes: 24, pinned: true },
        { title: 'Upcoming Regional Meetup (Poll)', author: 'Elena K.', replies: 45, likes: 18, pinned: false },
        { title: 'Vendor recommendations for event gear?', author: 'David C.', replies: 3, likes: 5, pinned: false },
      ].map((post, i) => (
        <div key={i} className="p-4 bg-white/5 rounded-xl border border-white/5 hover:bg-white/10 transition-colors dark:bg-slate-900">
          <div className="flex items-start justify-between mb-2">
            <h4 className="text-sm font-semibold text-white truncate max-w-[200px]">{post.title}</h4>
            {post.pinned && <Badge className="bg-orange-500/20 text-orange-400 border-none px-1.5 py-0 h-4 text-[9px] uppercase">Pinned</Badge>}
          </div>
          <div className="flex items-center justify-between text-[10px] text-slate-500 dark:text-slate-400 font-mono">
            <span>Posted by {post.author}</span>
            <div className="flex gap-3">
              <span className="flex items-center gap-1"><ThumbsUp size={10} /> {post.likes}</span>
              <span className="flex items-center gap-1"><MessageSquareReply size={10} /> {post.replies}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  </MockWindow>
);

const RequirementsMock = () => (
  <MockWindow className="w-full overflow-hidden relative">
    <div className="absolute top-1/2 -right-20 -translate-y-1/2 w-64 h-64 bg-lime-500/10 blur-3xl rounded-full" />
    <div className="flex items-center justify-between mb-8 relative z-10">
      <div>
        <h3 className="text-white font-bold text-lg tracking-tight">Compliance Tracking</h3>
        <p className="text-slate-400 text-xs">Requirements & Evidence</p>
      </div>
      <div className="p-2 bg-lime-500/20 rounded-lg text-lime-400"><CheckSquare size={20} /></div>
    </div>
    
    <div className="space-y-4 relative z-10">
      <div className="p-4 bg-white/5 rounded-xl border border-white/5 dark:bg-slate-900">
        <div className="flex justify-between items-center mb-2">
          <h4 className="text-sm font-semibold text-white">Annual Background Check</h4>
          <Badge className="bg-lime-500/20 text-lime-400 border-none px-2 py-0 h-5">Verified</Badge>
        </div>
        <div className="w-full bg-slate-800 rounded-full h-1.5 mb-2 mt-4">
          <div className="bg-lime-400 h-1.5 rounded-full" style={{ width: '100%' }}></div>
        </div>
        <span className="text-[10px] text-slate-500 dark:text-slate-400 font-mono">100% Complete</span>
      </div>

      <div className="p-4 bg-white/5 rounded-xl border border-white/5 border-l-4 border-l-amber-500 dark:bg-slate-900">
        <div className="flex justify-between items-center mb-2">
          <h4 className="text-sm font-semibold text-white">Ethical Training Module</h4>
          <Badge className="bg-amber-500/20 text-amber-400 border-none px-2 py-0 h-5">Pending Review</Badge>
        </div>
        <div className="text-[10px] text-slate-500 dark:text-slate-400 flex items-center gap-2 mt-3 bg-slate-900/50 p-2 rounded">
           <FileText size={12} className="text-amber-400" /> evidence_scan_04.pdf submitted
        </div>
      </div>
    </div>
  </MockWindow>
);

const CalendarMock = () => (
  <MockWindow className="w-full overflow-hidden relative">
    <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-indigo-500/10 to-transparent blur-3xl pointer-events-none" />
    <div className="flex items-center justify-between mb-8 relative z-10">
      <div>
        <h3 className="text-white font-bold text-lg tracking-tight">Global Calendar</h3>
        <p className="text-slate-400 text-xs">Events & RSVPs</p>
      </div>
      <div className="p-2 bg-indigo-500/20 rounded-lg text-indigo-400"><Calendar size={20} /></div>
    </div>
    
    <div className="space-y-4 relative z-10">
      <div className="flex gap-4">
         <div className="w-14 shrink-0 flex flex-col items-center justify-center bg-indigo-500/10 rounded-xl border border-indigo-500/20 py-2">
           <span className="text-[10px] text-indigo-400 font-bold uppercase">Nov</span>
           <span className="text-xl font-black text-white">12</span>
         </div>
         <div className="flex-1 bg-white/5 rounded-xl p-3 border border-white/5 dark:bg-slate-900">
           <div className="text-xs text-indigo-400 font-medium mb-1">10:00 AM - 2:00 PM EST</div>
           <h4 className="text-sm font-bold text-white mb-2">Q4 Regional Summit</h4>
           <div className="flex items-center gap-2">
             <div className="flex -space-x-1"><div className="w-5 h-5 rounded-full bg-slate-700 border border-slate-900"/><div className="w-5 h-5 rounded-full bg-slate-600 border border-slate-900"/></div>
             <span className="text-[10px] text-slate-500 dark:text-slate-400">142 Attending</span>
           </div>
         </div>
      </div>
    </div>
  </MockWindow>
);

const GoalsMock = () => (
  <MockWindow className="w-full overflow-hidden relative">
    <div className="absolute -left-20 -bottom-20 w-64 h-64 bg-pink-500/10 blur-3xl rounded-full" />
    <div className="flex items-center justify-between mb-8 relative z-10">
      <div>
        <h3 className="text-white font-bold text-lg tracking-tight">Performance Tracking</h3>
        <p className="text-slate-400 text-xs">OKRs & Metrics</p>
      </div>
      <div className="p-2 bg-pink-500/20 rounded-lg text-pink-400"><Target size={20} /></div>
    </div>
    
    <div className="space-y-4 relative z-10">
      <div className="p-4 bg-white/5 rounded-xl border border-white/5 relative overflow-hidden dark:bg-slate-900">
        <div className="absolute top-0 left-0 h-full w-[65%] bg-gradient-to-r from-pink-500/5 to-pink-500/10 border-r border-pink-500/20" />
        <div className="relative z-10">
          <div className="flex justify-between items-end mb-1">
            <h4 className="text-sm font-semibold text-white">Membership Drive 2026</h4>
            <span className="text-xs font-bold text-pink-400">65%</span>
          </div>
          <div className="text-[10px] text-slate-400 mb-3">Target: 5,000 new verified chapters</div>
          <div className="w-full bg-slate-800 rounded h-1">
            <div className="bg-pink-400 h-1 rounded shadow-[0_0_10px_rgba(244,114,182,0.5)]" style={{ width: '65%' }}></div>
          </div>
        </div>
      </div>
    </div>
  </MockWindow>
);

const SupportMock = () => (
  <MockWindow className="w-full overflow-hidden relative">
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-teal-500/10 blur-3xl rounded-full pointer-events-none" />
    <div className="flex items-center justify-between mb-8 relative z-10">
      <div>
        <h3 className="text-white font-bold text-lg tracking-tight">Help Desk</h3>
        <p className="text-slate-400 text-xs">Internal Ticketing</p>
      </div>
      <div className="p-2 bg-teal-500/20 rounded-lg text-teal-400"><LifeBuoy size={20} /></div>
    </div>
    
    <div className="space-y-3 relative z-10">
      <div className="p-3 bg-white/5 rounded-xl border border-white/5 flex gap-3 dark:bg-slate-900">
         <div className="w-2 h-2 rounded-full bg-teal-400 mt-1.5 shrink-0" />
         <div>
            <div className="text-sm font-bold text-white leading-tight">Can't access branch ledger</div>
            <div className="text-[10px] text-slate-500 dark:text-slate-400 mt-1 flex gap-2"><span>#REQ-9921</span> • <span>Assigned to: IT Ops</span></div>
         </div>
      </div>
    </div>
  </MockWindow>
);


const FundraiserMock = () => (
  <MockWindow className="w-full overflow-hidden relative">
    <div className="absolute top-0 right-0 w-64 h-64 bg-rose-500/10 blur-3xl rounded-full pointer-events-none" />
    <div className="flex items-center justify-between mb-8 relative z-10">
      <div>
        <h3 className="text-white font-bold text-lg tracking-tight">Campaigns</h3>
        <p className="text-slate-400 text-xs">Fundraisers & Donations</p>
      </div>
      <div className="p-2 bg-rose-500/20 rounded-lg text-rose-400"><HeartHandshake size={20} /></div>
    </div>
    
    <div className="space-y-4 relative z-10">
      <div className="p-4 bg-white/5 rounded-xl border border-white/5 dark:bg-slate-900">
        <div className="flex justify-between items-end mb-2">
           <div>
             <div className="text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-widest font-mono mb-1">Disaster Relief Fund</div>
             <div className="text-2xl font-black text-rose-400">$142,500<span className="text-sm text-slate-500 dark:text-slate-400 line-through ml-2">$200,000</span></div>
           </div>
           <Badge className="bg-rose-500/20 text-rose-400 border-none px-2 py-0 h-5 text-[10px]">Active</Badge>
        </div>
        <div className="w-full bg-slate-800 rounded-full h-1.5 mb-2 mt-2">
          <div className="bg-rose-400 h-1.5 rounded-full" style={{ width: '71%' }}></div>
        </div>
        <div className="text-[10px] text-slate-500 dark:text-slate-400 flex justify-between">
           <span>71% Funded</span>
           <span>12 days left</span>
        </div>
      </div>
      
      <div className="space-y-2">
        <span className="text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-widest px-2 font-mono">Recent Contributions</span>
        {[
          { name: 'Northern Org', amount: '$5,000', time: '10m ago' },
          { name: 'S. Vance', amount: '$250', time: '1h ago' },
        ].map((donation, i) => (
          <div key={i} className="flex justify-between items-center p-3 bg-white/5 border border-white/5 rounded-lg text-xs dark:bg-slate-900">
            <div className="flex items-center gap-3">
              <div className="w-6 h-6 rounded-full bg-slate-800 flex items-center justify-center text-[10px] text-slate-400 font-bold shrink-0">{donation.name.charAt(0)}</div>
              <span className="text-slate-300 font-medium">{donation.name}</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-rose-400 font-mono font-bold">{donation.amount}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  </MockWindow>
);

const GovernanceMock = () => (
  <MockWindow className="w-full overflow-hidden relative">
    <div className="absolute -top-20 -right-20 w-64 h-64 bg-amber-500/10 blur-3xl rounded-full pointer-events-none" />
    <div className="flex items-center justify-between mb-8 relative z-10">
      <div>
        <h3 className="text-white font-bold text-lg tracking-tight">Elections Center</h3>
        <p className="text-slate-400 text-xs">Secure Governance & Voting</p>
      </div>
      <div className="p-2 bg-amber-500/20 rounded-lg text-amber-400"><Landmark size={20} /></div>
    </div>
    
    <div className="space-y-4 relative z-10">
      <div className="p-4 bg-white/5 rounded-xl border border-white/5 dark:bg-slate-900">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h4 className="text-sm font-semibold text-white">2026 Executive Board Election</h4>
            <div className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5 flex gap-2"><span>Ends in 3 days</span> • <span>Secret Ballot</span></div>
          </div>
          <Badge className="bg-amber-500/20 text-amber-400 border-none px-2 py-0 h-5">Open for Voting</Badge>
        </div>
        
        <div className="space-y-2 mt-4">
          {[
            { name: 'Sarah Chen', role: 'Current Treasurer', selected: true },
            { name: 'James Wilson', role: 'V.P. of Operations', selected: false },
          ].map((candidate, i) => (
             <div key={i} className={`p-3 rounded-lg border ${candidate.selected ? 'bg-amber-500/10 border-amber-500/30' : 'bg-slate-900/50 border-white/5'} flex items-center gap-3 cursor-pointer`}>
                <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${candidate.selected ? 'border-amber-500 bg-amber-500' : 'border-slate-600 bg-slate-800'}`}>
                  {candidate.selected && <div className="w-1.5 h-1.5 bg-slate-900 rounded-full" />}
                </div>
                <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center text-xs font-bold text-slate-300">
                  {candidate.name.charAt(0)}
                </div>
                <div>
                  <div className={`text-sm font-medium ${candidate.selected ? 'text-amber-400' : 'text-slate-300'}`}>{candidate.name}</div>
                  <div className="text-[10px] text-slate-500 dark:text-slate-400">{candidate.role}</div>
                </div>
             </div>
          ))}
        </div>
        
        <div className="mt-4 pt-3 border-t border-white/10 flex justify-end">
           <Button className="bg-amber-500 hover:bg-amber-600 text-slate-900 dark:text-white text-xs h-8 px-4 font-bold border-none">
             Cast Secure Vote
           </Button>
        </div>
      </div>
    </div>
  </MockWindow>
);


const ModulesMock = () => (
  <MockWindow className="w-full relative overflow-hidden">
    <div className="absolute -top-20 -right-20 w-64 h-64 bg-blue-500/10 blur-3xl rounded-full pointer-events-none" />
    <div className="flex items-center justify-between mb-8 relative z-10">
      <div>
        <h3 className="text-white font-bold text-lg tracking-tight">Ecosystem Apps</h3>
        <p className="text-slate-400 text-xs">Enable features your org needs</p>
      </div>
      <div className="p-2 bg-blue-500/20 rounded-lg text-blue-400"><Blocks size={20} /></div>
    </div>
    
    <div className="grid grid-cols-2 gap-3 relative z-10">
      {[
        { name: 'Discussions', icon: MessagesSquare, enabled: true },
        { name: 'Polls & Votes', icon: BarChart3, enabled: true },
        { name: 'Task Boards', icon: LayoutList, enabled: false },
        { name: 'Document Vault', icon: FolderLock, enabled: true }
      ].map((app, i) => (
        <div key={i} className={`p-3 rounded-xl border flex flex-col gap-3 ${app.enabled ? 'bg-blue-500/10 border-blue-500/30' : 'bg-slate-900/50 border-white/5'}`}>
           <div className="flex justify-between items-start">
             <app.icon size={16} className={app.enabled ? 'text-blue-400' : 'text-slate-500 dark:text-slate-400'} />
             <div className={`w-8 h-4 rounded-full flex items-center px-0.5 ${app.enabled ? 'bg-blue-500' : 'bg-slate-700'}`}>
               <div className={`w-3 h-3 rounded-full bg-white dark:bg-slate-900 shadow-sm transform transition-transform ${app.enabled ? 'translate-x-4' : 'translate-x-0'}`} />
             </div>
           </div>
           <span className={`text-xs font-medium ${app.enabled ? 'text-white' : 'text-slate-400'}`}>{app.name}</span>
        </div>
      ))}
    </div>
  </MockWindow>
);

const GrowthMock = () => (
  <MockWindow className="w-full relative overflow-hidden">
    <div className="absolute -top-20 -right-20 w-64 h-64 bg-purple-500/10 blur-3xl rounded-full pointer-events-none" />
    <div className="flex items-center justify-between mb-8 relative z-10">
      <div>
        <h3 className="text-white font-bold text-lg tracking-tight">Growth & Referrals</h3>
        <p className="text-slate-400 text-xs">Track expansion</p>
      </div>
      <div className="p-2 bg-purple-500/20 rounded-lg text-purple-400"><TrendingUp size={20} /></div>
    </div>
    
    <div className="space-y-4 relative z-10">
      <div className="flex gap-4">
        <div className="flex-1 p-3 bg-white/5 border border-white/5 rounded-xl dark:bg-slate-900">
           <div className="text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-widest font-mono mb-1">Invites Sent</div>
           <div className="text-xl font-black text-white">4,092</div>
        </div>
        <div className="flex-1 p-3 bg-purple-500/10 border border-purple-500/20 rounded-xl">
           <div className="text-[10px] text-purple-300 uppercase tracking-widest font-mono mb-1">New Chapters</div>
           <div className="text-xl font-black text-purple-400">+142</div>
        </div>
      </div>
      
      <div className="p-4 bg-white/5 border border-white/5 rounded-xl relative overflow-hidden dark:bg-slate-900">
        <div className="absolute top-0 right-0 p-2"><TrendingUp size={64} className="text-white/[0.02]" /></div>
        <div className="text-[10px] text-slate-500 dark:text-slate-400 flex justify-between mb-2">
           <span>Top Recruiting Regions</span>
           <span className="text-purple-400">View All</span>
        </div>
        <div className="space-y-2">
           {['Northern America', 'Western Europe', 'APAC'].map((r, i) => (
             <div key={i} className="flex justify-between items-center relative z-10">
               <span className="text-xs text-slate-300">{r}</span>
               <div className="flex-1 max-w-[100px] mx-3 h-1.5 bg-slate-800 rounded-full overflow-hidden">
                 <div className="bg-purple-400 h-full" style={{ width: `${90 - (i * 20)}%` }} />
               </div>
               <span className="text-xs font-mono text-slate-500 dark:text-slate-400">+{82 - (i*15)}</span>
             </div>
           ))}
        </div>
      </div>
    </div>
  </MockWindow>
);


// --- Main Component ---

export const ShowcasePage = () => {
  const brandName = 'OrgSets';
  const { scrollYProgress } = useScroll();
  const opacity = useTransform(scrollYProgress, [0, 0.15], [1, 0]);

  return (
    <div className="min-h-screen font-sans selection:bg-brand-primary/20 bg-slate-50 text-slate-900 dark:text-white dark:bg-slate-950">
      <SEO 
        title={`Showcase | Organization Management Platform | ${brandName}`}
        description={`See exactly how ${brandName} powers the world's most complex organizations with unified finance, identity, and communication.`}
      />
      <PublicHeader />

      {/* Hero Section */}
      <section className="grid grid-cols-1 lg:grid-cols-2 border-b-[8px] border-black dark:border-white min-h-[50vh] bg-black text-white dark:bg-white dark:text-black mb-24">
        <div className="p-8 pt-28 md:p-16 md:pt-28 lg:p-12 lg:pt-28 xl:p-24 xl:pt-28 flex flex-col justify-center h-full border-b-[8px] lg:border-b-0 lg:border-r-[8px] border-black dark:border-white bg-white dark:bg-black text-black dark:text-white">
          <h1 className="text-5xl sm:text-6xl md:text-8xl lg:text-6xl xl:text-7xl 2xl:text-[100px] font-black uppercase tracking-tighter leading-[0.85] mb-8 break-words">
            Platform <br/> Showcase.
          </h1>
          <p className="text-xl md:text-2xl font-bold leading-snug mb-12">
            Stop chasing messages across WhatsApp or searching for spreadsheets that no one can find. {brandName} brings your group's tools together in one simple, private, and secure place.
          </p>
        </div>
        <div className="p-8 md:p-16 flex flex-col justify-center items-center h-full relative overflow-hidden bg-brand-primary text-white">
          <div className="absolute top-0 right-0 w-64 h-64 bg-pink-500 rounded-bl-full border-b-[8px] border-l-[8px] border-black dark:border-white" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-brand-secondary rounded-tr-full border-t-[8px] border-r-[8px] border-black dark:border-white" />
          
          <div className="relative z-10 w-full max-w-md space-y-4">
            <div className="border-[4px] border-black dark:border-white bg-white dark:bg-black text-black dark:text-white p-6 font-black uppercase tracking-tight shadow-[8px_8px_0px_#000] dark:shadow-[8px_8px_0px_#fff]">
              <div className="text-xs font-mono font-black text-[#0044FF] dark:text-[#FFD600] uppercase tracking-widest mb-1">
                Interactive Architecture
              </div>
              <div className="text-3xl font-display font-black leading-tight">
                16 Core Modules. <br />
                <span className="text-[#0044FF] dark:text-[#FFD600]">1 Unified Platform.</span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="border-[3px] border-black dark:border-white bg-[#FFD600] text-black p-4 font-black uppercase text-center shadow-[4px_4px_0px_#000]">
                <div className="text-2xl font-black font-display leading-none">100%</div>
                <div className="text-[10px] font-mono tracking-widest mt-1">Audit-Ready</div>
              </div>
              <div className="border-[3px] border-black dark:border-white bg-black text-white dark:bg-white dark:text-black p-4 font-black uppercase text-center shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#fff]">
                <div className="text-2xl font-black font-display leading-none">Zero</div>
                <div className="text-[10px] font-mono tracking-widest mt-1">Spreadsheets</div>
              </div>
            </div>
          </div>
        </div>
      </section>
      

      {/* Feature 1: Hierarchy */}
      <section id="feature-hierarchy" className="py-24 px-4 overflow-hidden bg-slate-50 dark:bg-slate-950">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <motion.div 
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            className="order-2 lg:order-1"
          >
            <HierarchyMock />
          </motion.div>
          
          <div className="order-1 lg:order-2 space-y-6">
            <div className="inline-flex items-center justify-center p-3 bg-brand-primary/10 text-brand-primary rounded-xl mb-4">
              <Network size={28} strokeWidth={2.5} />
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-slate-900 dark:text-white tracking-tight leading-[1.1]">
              Your hierarchy, your rules <br /><span className="italic text-brand-primary">— nested orgs, teams, chapters.</span>
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
              No more messy spreadsheets or confusion. {brandName} helps you organize your clubs, teams, and family groups into one clear place that actually makes sense.
            </p>
            <ul className="space-y-4 pt-4">
              {[
                "Simple setup for any group size.",
                "Easy-to-understand structure.",
                "Get a clear view of your whole community."
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-slate-700 dark:text-slate-300">
                  <CheckCircle2 className="text-brand-primary shrink-0 mt-1" size={20} />
                  <span className="font-medium text-lg">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Feature 2: Ledger */}
      <section id="feature-ledger" className="py-24 px-4 overflow-hidden bg-white dark:bg-slate-900 border-y border-slate-200">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="space-y-6">
            <div className="inline-flex items-center justify-center p-3 bg-emerald-500/10 text-emerald-600 rounded-xl mb-4">
              <CreditCard size={28} strokeWidth={2.5} />
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-slate-900 dark:text-white tracking-tight leading-[1.1]">
              Track dues, fees, and transactions <br /><span className="italic text-emerald-600">with PII protection.</span>
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
              No more stressing about dues or wondering where the money went. Track everything clearly and keep your group's budget simple.
            </p>
            <ul className="space-y-4 pt-4">
              {[
                "Easy ways to collect dues.",
                "Clear view of your group's money.",
                "No more lost receipts or confusion."
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-slate-700 dark:text-slate-300">
                  <CheckCircle2 className="text-emerald-500 shrink-0 mt-1" size={20} />
                  <span className="font-medium text-lg">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <motion.div 
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
          >
            <LedgerMock />
          </motion.div>
        </div>
      </section>

      {/* Feature 3: Pulse & Comms */}
      <section id="feature-pulse" className="py-24 px-4 overflow-hidden bg-slate-50 dark:bg-slate-950">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <motion.div 
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            className="order-2 lg:order-1"
          >
            <PulseMock />
          </motion.div>
          
          <div className="order-1 lg:order-2 space-y-6">
            <div className="inline-flex items-center justify-center p-3 bg-rose-500/10 text-rose-600 rounded-xl mb-4">
              <Rss size={28} strokeWidth={2.5} />
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-slate-900 dark:text-white tracking-tight leading-[1.1]">
              Post announcements to all members <br /><span className="italic text-rose-600">or just one chapter.</span>
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
              Tired of important messages getting lost in group chats? Keep updates in one spot where everyone is guaranteed to see them.
            </p>
            <ul className="space-y-4 pt-4">
              {[
                "Important news is always visible.",
                "Know who has seen your update.",
                "Share updates that really matter."
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-slate-700 dark:text-slate-300">
                  <CheckCircle2 className="text-rose-500 shrink-0 mt-1" size={20} />
                  <span className="font-medium text-lg">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Feature 4: Vault */}
      <section id="feature-vault" className="py-24 px-4 overflow-hidden bg-white dark:bg-slate-900 border-t border-slate-200">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="space-y-6">
            <div className="inline-flex items-center justify-center p-3 bg-violet-500/10 text-violet-600 rounded-xl mb-4">
              <Shield size={28} strokeWidth={2.5} />
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-slate-900 dark:text-white tracking-tight leading-[1.1]">
              Store bylaws, compliance docs, <br /><span className="italic text-violet-600">and board records securely.</span>
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
              Keep all your club's important files, member lists, and photos in one safe place that only your members can access. It’s like a digital filing cabinet for your whole group.
            </p>
            <ul className="space-y-4 pt-4">
              {[
                "Publish globally to all descending orgs.",
                "Share selectively with parent or child orgs.",
                "Keep sensitive assets private to your local chapter."
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-slate-700 dark:text-slate-300">
                  <CheckCircle2 className="text-violet-500 shrink-0 mt-1" size={20} />
                  <span className="font-medium text-lg">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <motion.div 
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
          >
            <VaultMock />
          </motion.div>
        </div>
      </section>

      {/* Feature 5: Identity Passport */}
      <section id="feature-identity" className="py-24 px-4 overflow-hidden bg-slate-50 dark:bg-slate-950 border-t border-slate-200">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <motion.div 
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            className="order-2 lg:order-1"
          >
            <DirectoryMock />
          </motion.div>
          
          <div className="order-1 lg:order-2 space-y-6">
            <div className="inline-flex items-center justify-center p-3 bg-cyan-500/10 text-cyan-600 rounded-xl mb-4">
              <Users size={28} strokeWidth={2.5} />
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-slate-900 dark:text-white tracking-tight leading-[1.1]">
              Know Your <span className="italic text-cyan-600">Members.</span>
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
              Always know who is part of your group. {brandName} provides a simple member list so you can easily contact the right people without needing a complex database.
            </p>
            <ul className="space-y-4 pt-4">
              {[
                "Immutable member passports and vetting history.",
                "Dynamic roles tied to specific orgs.",
                "Cross-chapter visibility based on privacy settings."
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-slate-700 dark:text-slate-300">
                  <CheckCircle2 className="text-cyan-500 shrink-0 mt-1" size={20} />
                  <span className="font-medium text-lg">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Feature 6: Discussion */}
      <section id="feature-discussion" className="py-24 px-4 overflow-hidden bg-white dark:bg-slate-900 border-t border-slate-200">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="space-y-6">
            <div className="inline-flex items-center justify-center p-3 bg-orange-500/10 text-orange-600 rounded-xl mb-4">
              <MessagesSquare size={28} strokeWidth={2.5} />
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-slate-900 dark:text-white tracking-tight leading-[1.1]">
              Structured <span className="italic text-orange-600">Discussions.</span>
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
              Move important conversations out of chaotic group chats. The Message Board provides a structured forum for long-form discussions, feedback, and planning across your organization.
            </p>
            <ul className="space-y-4 pt-4">
              {[
                "Organized topics with threaded replies.",
                "Pin important discussions for visibility.",
                "Rich text support and file attachments."
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-slate-700 dark:text-slate-300">
                  <CheckCircle2 className="text-orange-500 shrink-0 mt-1" size={20} />
                  <span className="font-medium text-lg">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <motion.div 
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
          >
            <DiscussionMock />
          </motion.div>
        </div>
      </section>
      

      {/* Feature 7: Real-Time Chat */}
      <section id="feature-chat" className="py-24 px-4 overflow-hidden bg-slate-50 dark:bg-slate-950 border-t border-slate-200">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <motion.div 
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            className="order-2 lg:order-1"
          >
            <ChatMock />
          </motion.div>
          
          <div className="order-1 lg:order-2 space-y-6">
            <div className="inline-flex items-center justify-center p-3 bg-sky-500/10 text-sky-600 rounded-xl mb-4">
              <MessageCircle size={28} strokeWidth={2.5} />
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-slate-900 dark:text-white tracking-tight leading-[1.1]">
              Instant <span className="italic text-sky-600">Collaboration.</span>
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
              When you need quick answers, skip the email. Built-in real-time chat means your team can collaborate instantly without leaving the platform or switching contexts.
            </p>
            <ul className="space-y-4 pt-4">
              {[
                "Direct messages or group channels.",
                "Real-time typing indicators and read states.",
                "Secure and strictly contained within your organization."
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-slate-700 dark:text-slate-300">
                  <CheckCircle2 className="text-sky-500 shrink-0 mt-1" size={20} />
                  <span className="font-medium text-lg">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Feature 8: Newsletter Studio */}
      <section id="feature-newsletter" className="py-24 px-4 overflow-hidden bg-white dark:bg-slate-900 border-t border-slate-200">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="space-y-6">
            <div className="inline-flex items-center justify-center p-3 bg-fuchsia-500/10 text-fuchsia-600 rounded-xl mb-4">
              <LayoutTemplate size={28} strokeWidth={2.5} />
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-slate-900 dark:text-white tracking-tight leading-[1.1]">
              Multichannel <span className="italic text-fuchsia-600">Publishing.</span>
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
              Reach your members where they are. Design beautiful updates in the Studio and push them out via Email, SMS, or directly into the app's Pulse Feed all at once.
            </p>
            <ul className="space-y-4 pt-4">
              {[
                "Drag-and-drop newsletter builder.",
                "Simultaneous email and SMS delivery.",
                "Detailed open rates and engagement tracking."
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-slate-700 dark:text-slate-300">
                  <CheckCircle2 className="text-fuchsia-500 shrink-0 mt-1" size={20} />
                  <span className="font-medium text-lg">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <motion.div 
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
          >
            <NewsletterMock />
          </motion.div>
        </div>
      </section>

      {/* Feature 9: Requirements & Compliance */}
      <section id="feature-compliance" className="py-24 px-4 overflow-hidden bg-slate-50 dark:bg-slate-950 border-t border-slate-200">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <motion.div 
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            className="order-2 lg:order-1"
          >
            <RequirementsMock />
          </motion.div>
          
          <div className="order-1 lg:order-2 space-y-6">
            <div className="inline-flex items-center justify-center p-3 bg-lime-500/10 text-lime-600 rounded-xl mb-4">
              <CheckSquare size={28} strokeWidth={2.5} />
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-slate-900 dark:text-white tracking-tight leading-[1.1]">
              Compliance <span className="italic text-lime-600">Tracking.</span>
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
              Ensure every member and chapter meets your global standards. Track background checks, training modules, and annual certifications securely in one place.
            </p>
            <ul className="space-y-4 pt-4">
              {[
                "Automated reminders for expiring credentials.",
                "Secure evidence upload and verification workflows.",
                "Real-time compliance heatmaps for the entire organization."
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-slate-700 dark:text-slate-300">
                  <CheckCircle2 className="text-lime-500 shrink-0 mt-1" size={20} />
                  <span className="font-medium text-lg">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Feature 10: Global Calendar */}
      <section id="feature-calendar" className="py-24 px-4 overflow-hidden bg-white dark:bg-slate-900 border-t border-slate-200">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="space-y-6">
            <div className="inline-flex items-center justify-center p-3 bg-indigo-500/10 text-indigo-600 rounded-xl mb-4">
              <Calendar size={28} strokeWidth={2.5} />
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-slate-900 dark:text-white tracking-tight leading-[1.1]">
              Unified <span className="italic text-indigo-600">Calendar.</span>
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
              Eliminate scheduling conflicts. A centralized event system where global events cascade down, and local events selectively sync up, providing total visibility.
            </p>
            <ul className="space-y-4 pt-4">
              {[
                "Hierarchical event visibility.",
                "Integrated RSVPs and ticket sales.",
                "Automatic calendar sync (Google, Outlook, Apple)."
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-slate-700 dark:text-slate-300">
                  <CheckCircle2 className="text-indigo-500 shrink-0 mt-1" size={20} />
                  <span className="font-medium text-lg">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <motion.div 
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
          >
            <CalendarMock />
          </motion.div>
        </div>
      </section>

      {/* Feature 11: Performance & Goals */}
      <section id="feature-goals" className="py-24 px-4 overflow-hidden bg-slate-50 dark:bg-slate-950 border-t border-slate-200">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <motion.div 
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            className="order-2 lg:order-1"
          >
            <GoalsMock />
          </motion.div>
          
          <div className="order-1 lg:order-2 space-y-6">
            <div className="inline-flex items-center justify-center p-3 bg-pink-500/10 text-pink-600 rounded-xl mb-4">
              <Target size={28} strokeWidth={2.5} />
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-slate-900 dark:text-white tracking-tight leading-[1.1]">
              Cascading <span className="italic text-pink-600">Goals.</span>
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
              Align every chapter with HQ objectives. Set OKRs at the top level, pass them down through the hierarchy, and watch progress automatically roll up from local initiatives to global dashboards.
            </p>
            <ul className="space-y-4 pt-4">
              {[
                "Cascading OKRs across multiple org levels.",
                "Automated progress metrics from integrated data.",
                "Leaderboards to drive healthy chapter competition."
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-slate-700 dark:text-slate-300">
                  <CheckCircle2 className="text-pink-500 shrink-0 mt-1" size={20} />
                  <span className="font-medium text-lg">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Feature 12: Help Desk */}
      <section id="feature-support" className="py-24 px-4 overflow-hidden bg-white dark:bg-slate-900 border-t border-slate-200">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="space-y-6">
            <div className="inline-flex items-center justify-center p-3 bg-teal-500/10 text-teal-600 rounded-xl mb-4">
              <LifeBuoy size={28} strokeWidth={2.5} />
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-slate-900 dark:text-white tracking-tight leading-[1.1]">
              Internal <span className="italic text-teal-600">Help Desk.</span>
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
              Never let member issues fall through the cracks. Built-in ticketing allows members to request assistance from HQ or their regional administrators seamlessly.
            </p>
            <ul className="space-y-4 pt-4">
              {[
                "Route tickets to specific departments (IT, Finance, Legal).",
                "SLA tracking and automated escalations.",
                "Knowledge base integration for deflection."
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-slate-700 dark:text-slate-300">
                  <CheckCircle2 className="text-teal-500 shrink-0 mt-1" size={20} />
                  <span className="font-medium text-lg">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <motion.div 
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
          >
            <SupportMock />
          </motion.div>
        </div>
      </section>

      {/* Feature 13: Fundraisers */}
      <section id="feature-fundraisers" className="py-24 px-4 overflow-hidden bg-slate-50 dark:bg-slate-950 border-t border-slate-200">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <motion.div 
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            className="order-2 lg:order-1"
          >
            <FundraiserMock />
          </motion.div>
          
          <div className="order-1 lg:order-2 space-y-6">
            <div className="inline-flex items-center justify-center p-3 bg-rose-500/10 text-rose-600 rounded-xl mb-4">
              <HeartHandshake size={28} strokeWidth={2.5} />
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-slate-900 dark:text-white tracking-tight leading-[1.1]">
              Network <span className="italic text-rose-600">Fundraising.</span>
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
              Launch massive capital campaigns that span the globe. Set parent-level goals and let child-nodes collectively contribute via their own fundraising pages.
            </p>
            <ul className="space-y-4 pt-4">
              {[
                "Peer-to-peer donation processing.",
                "Automated contribution milestones and recognition.",
                "Real-time global tracker that updates instantly."
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-slate-700 dark:text-slate-300">
                  <CheckCircle2 className="text-rose-500 shrink-0 mt-1" size={20} />
                  <span className="font-medium text-lg">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Feature 14: Governance & Elections */}
      <section id="feature-governance" className="py-24 px-4 overflow-hidden bg-white dark:bg-slate-900 border-t border-slate-200">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="space-y-6">
            <div className="inline-flex items-center justify-center p-3 bg-amber-500/10 text-amber-600 rounded-xl mb-4">
              <Landmark size={28} strokeWidth={2.5} />
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-slate-900 dark:text-white tracking-tight leading-[1.1]">
              Run elections, votes, and decisions <br /><span className="italic text-amber-600">— fully audited.</span>
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
              Run fully digital, verifiable elections for leadership, board positions, and policy votes directly from your organization's dashboard.
            </p>
            <ul className="space-y-4 pt-4">
              {[
                "Strict one-vote-per-member enforcement.",
                "Anonymous and verifiable ballot processing.",
                "Candidate profiles and nomination periods."
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-slate-700 dark:text-slate-300">
                  <CheckCircle2 className="text-amber-500 shrink-0 mt-1" size={20} />
                  <span className="font-medium text-lg">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <motion.div 
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
          >
            <GovernanceMock />
          </motion.div>
        </div>
      </section>

      {/* Feature 15: Modules */}
      <section id="feature-modules" className="py-24 px-4 overflow-hidden bg-slate-50 dark:bg-slate-950 border-t border-slate-200">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <motion.div 
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            className="order-2 lg:order-1"
          >
            <ModulesMock />
          </motion.div>
          <div className="order-1 lg:order-2 space-y-6">
            <div className="inline-flex items-center justify-center p-3 bg-blue-500/10 text-blue-600 rounded-xl mb-4">
              <Blocks size={28} strokeWidth={2.5} />
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-slate-900 dark:text-white tracking-tight leading-[1.1]">
              Simple <span className="italic text-blue-600">Modules.</span>
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
              Keep the interface clean and intuitive. Turn on just the tools your groups actually need—like polls or check-ins—and hide the rest.
            </p>
            <ul className="space-y-4 pt-4">
              {[
                "Enable/disable apps per-node.",
                "Unified ecosystem across your organization.",
                "No clutter for simple chapters."
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-slate-700 dark:text-slate-300">
                  <CheckCircle2 className="text-blue-500 shrink-0 mt-1" size={20} />
                  <span className="font-medium text-lg">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Feature 16: Growth Engine */}
      <section id="feature-growth" className="py-24 px-4 overflow-hidden bg-white dark:bg-slate-900 border-t border-slate-200">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="space-y-6">
            <div className="inline-flex items-center justify-center p-3 bg-purple-500/10 text-purple-600 rounded-xl mb-4">
              <TrendingUp size={28} strokeWidth={2.5} />
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-slate-900 dark:text-white tracking-tight leading-[1.1]">
              Growth <span className="italic text-purple-600">Engine.</span>
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
              Track network expansion and reward the teams who are championing your mission the loudest with built-in referral and invite tracking.
            </p>
            <ul className="space-y-4 pt-4">
              {[
                "Track invite conversion rates.",
                "Custom referral links for nodes.",
                "Gamify chapter expansion."
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-slate-700 dark:text-slate-300">
                  <CheckCircle2 className="text-purple-500 shrink-0 mt-1" size={20} />
                  <span className="font-medium text-lg">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <motion.div 
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
          >
            <GrowthMock />
          </motion.div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-32 px-4 text-center bg-slate-900 text-white selection:bg-brand-primary/30 relative overflow-hidden">
        <div className="absolute inset-0 bg-brand-primary/10 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-brand-primary/20 via-slate-900 to-slate-900 pointer-events-none" />
        <div className="max-w-4xl mx-auto relative z-10">
          <h2 className="text-5xl md:text-7xl font-black tracking-tight mb-8">
            Ready for <span className="text-blue-400">Command?</span>
          </h2>
          <p className="text-xl text-slate-400 mb-12 max-w-2xl mx-auto">
            These features are just the beginning. Discover modules for shared vaults, event calendars, goals tracking, and more.
          </p>
          <Button 
            size="lg" 
            className="h-20 px-16 text-2xl font-black bg-white dark:bg-slate-900 text-slate-900 dark:text-white hover:bg-slate-200 rounded-2xl shadow-2xl group uppercase tracking-widest hover:-translate-y-1 transition-all duration-300"
            onClick={() => window.location.href = '/sign-up'}
          >
            Deploy {brandName}
            <ArrowRight className="ml-3 group-hover:translate-x-2 transition-transform" />
          </Button>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};
